import { displayTitle, type CatalogueAct, type SourceVerification } from '@buakei/types';
import {
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  Notice,
  PermissionDeniedState,
  VerificationDimensions,
  buttonVariants,
} from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Download, FileText, MessageSquareText } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import type { IdentityClient } from '@/auth/identity-client';
import { useAuth } from '@/auth/session';
import { ActsAssistant } from '@/assistant/ActsAssistant';
import { ReportProblem } from '@/assistant/ReportProblem';
import { STATUS_LABEL } from '@/pages/editorial/status';
import { libraryApi } from '@/services/api';
import { PDF_FILE, saveFile } from '@/services/save-file';
import { Page } from '@/shell/Page';

/**
 * Legal library (M3, ADR-012, ADR-014): the original Acts, exactly as published, grouped by
 * year. Extracted text is kept on the server for search only and is not shown here. Everything
 * is provisional and unverified. The Worker decides what may be served; this page displays it.
 */

const REFRESH_MS = 60_000;
const UNDATED = 'undated';

/**
 * Tokens for reading the library: any signed-in account with a verified email (ADR-016).
 * Experience only; the Worker re-checks every request.
 */
export function useReaderTokens(): IdentityClient | null {
  const { session, accounts } = useAuth();
  return session?.provider === 'firebase' && session.emailVerified ? accounts : null;
}

export function useCatalogue(tokens: IdentityClient | null) {
  return useQuery({
    queryKey: ['library', 'catalogue'],
    queryFn: () => libraryApi.catalogue(tokens!),
    enabled: tokens !== null,
    refetchInterval: REFRESH_MS,
  });
}

/** Acts grouped by year, newest first; undated Acts last. */
function byYear(acts: CatalogueAct[]): { key: string; label: string; acts: CatalogueAct[] }[] {
  const groups = new Map<string, CatalogueAct[]>();
  for (const a of acts) {
    const key = a.year === null ? UNDATED : String(a.year);
    groups.set(key, [...(groups.get(key) ?? []), a]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === UNDATED ? 1 : b === UNDATED ? -1 : Number(b) - Number(a)))
    .map(([key, list]) => ({
      key,
      label: key === UNDATED ? 'Undated' : key,
      acts: [...list].sort((x, y) => x.title.localeCompare(y.title)),
    }));
}

/** Why the library is closed to this session, with the way forward. */
export function LibraryClosedNotice() {
  const { session } = useAuth();
  return session?.provider === 'firebase' ? (
    <PermissionDeniedState title="Verify your email address to open the library">
      <p>
        Follow the link in the verification email, then sign in again.{' '}
        <Link className="font-medium text-navy-900 underline" to="/account">
          View your account
        </Link>
      </p>
    </PermissionDeniedState>
  ) : (
    <PermissionDeniedState title="Sign in with your Buakei AI account to read the library">
      <p>DEMO personas use demonstration data only; the library holds real documents.</p>
    </PermissionDeniedState>
  );
}

export function Denied() {
  return (
    <Page title="Library">
      <LibraryClosedNotice />
    </Page>
  );
}

export function ProvisionalNotice() {
  return (
    <Notice title="Provisional and unverified" icon={AlertTriangle}>
      These are the documents as collected. Their authenticity, accuracy and current legal status
      have not been reviewed, and rights for these sources are pending permission.
    </Notice>
  );
}

export function ActList({ acts }: { acts: CatalogueAct[] }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {acts.map((a) => (
        <li key={a.versionId}>
          <Link
            to={`/library/${a.versionId}`}
            className="flex h-full items-start gap-3 rounded-xl border border-line-200 bg-white p-4 hover:border-navy-900 hover:bg-navy-50"
          >
            <FileText className="mt-0.5 size-5 shrink-0 text-navy-900" aria-hidden />
            <span className="flex flex-col gap-1">
              <span className="font-medium text-navy-900">{displayTitle(a.title)}</span>
              <span className="text-sm text-muted-600">{a.year ?? 'Undated'} · PDF</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Back links for a document page. Opened from an Ask Buakei answer, it leads back to that
 * chat first (the chat is in the address it came from); the collection link stays too.
 */
export function BackLinks({
  fallback,
  fallbackLabel,
}: {
  fallback: string;
  fallbackLabel: string;
}) {
  const state = useLocation().state as { backTo?: unknown } | null;
  const chat =
    typeof state?.backTo === 'string' && state.backTo.startsWith('/') ? state.backTo : null;
  const linkClass = 'flex items-center gap-1 text-sm font-medium text-navy-900 underline';
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      {chat ? (
        <Link to={chat} className={linkClass}>
          <ArrowLeft className="size-4" aria-hidden /> Back to your chat
        </Link>
      ) : null}
      <Link to={fallback} className={linkClass}>
        {chat ? null : <ArrowLeft className="size-4" aria-hidden />} {fallbackLabel}
      </Link>
    </div>
  );
}

/** Acts and Cases are the library's two collections (ADR-018). */
export function LibraryTabs({ current }: { current: 'acts' | 'cases' }) {
  const tab = (key: 'acts' | 'cases', label: string, to: string) => (
    <Link
      to={to}
      aria-current={current === key ? 'page' : undefined}
      className={
        current === key
          ? '-mb-px border-b-2 border-navy-900 px-4 py-2 text-sm font-semibold text-navy-900'
          : '-mb-px border-b-2 border-transparent px-4 py-2 text-sm font-medium text-muted-600 hover:text-navy-900'
      }
    >
      {label}
    </Link>
  );
  return (
    <nav aria-label="Library collections" className="flex gap-1 border-b border-line-200">
      {tab('acts', 'Acts', '/library')}
      {tab('cases', 'Cases', '/library/cases')}
    </nav>
  );
}

export function LibraryPage() {
  const tokens = useReaderTokens();
  const catalogue = useCatalogue(tokens);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const searchId = useId();
  const year = params.get('year');

  const groups = useMemo(() => byYear(catalogue.data?.acts ?? []), [catalogue.data]);
  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return null;
    return byYear(
      (catalogue.data?.acts ?? []).filter((a) =>
        `${a.title} ${displayTitle(a.title)} ${a.year ?? ''}`.toLowerCase().includes(term),
      ),
    );
  }, [catalogue.data, query]);

  if (!tokens) return <Denied />;
  const selected = year ? groups.find((g) => g.key === year) : undefined;
  const total = catalogue.data?.acts.length ?? 0;
  const matchCount = matches?.reduce((n, g) => n + g.acts.length, 0) ?? 0;

  return (
    <Page
      title="Library"
      description="Acts and judgments of Sierra Leone, as published."
      width="wide"
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/library/cite-check" className={buttonVariants({ variant: 'secondary' })}>
            Check citations
          </Link>
          <Link to="/library/ask" className={buttonVariants()}>
            <MessageSquareText aria-hidden /> Ask Buakei
          </Link>
        </div>
      }
    >
      <LibraryTabs current="acts" />
      <ProvisionalNotice />
      {catalogue.isPending ? <LoadingState label="Loading the library" /> : null}
      {catalogue.isError ? (
        <ErrorState
          title="The library could not be loaded"
          onRetry={() => void catalogue.refetch()}
        >
          {catalogue.error.message}
        </ErrorState>
      ) : null}
      {catalogue.data ? (
        <>
          {catalogue.data.withheldByRightsGate ? (
            <p className="text-sm text-muted-600">
              Some Acts are withheld on this deployment until permission is recorded.
            </p>
          ) : null}
          <div className="flex flex-col gap-1">
            <label htmlFor={searchId} className="text-sm font-medium text-ink-900">
              Search by title or year
            </label>
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-10 rounded-md border border-line-200 bg-white px-3 text-ink-900"
            />
          </div>

          {matches ? (
            <>
              <p className="text-sm text-ink-900" role="status" aria-live="polite">
                {matchCount} of {total} Acts match
              </p>
              {matchCount === 0 ? (
                <EmptyState title="No Acts match">Try a different title or year.</EmptyState>
              ) : (
                matches.map((g) => (
                  <section
                    key={g.key}
                    aria-labelledby={`year-${g.key}`}
                    className="flex flex-col gap-3"
                  >
                    <h2 id={`year-${g.key}`} className="font-display text-xl text-navy-900">
                      {g.label}
                    </h2>
                    <ActList acts={g.acts} />
                  </section>
                ))
              )}
            </>
          ) : year ? (
            <section aria-labelledby="year-heading" className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setParams({})}
                className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
              >
                <ArrowLeft className="size-4" aria-hidden /> All years
              </button>
              <h2 id="year-heading" className="font-display text-2xl text-navy-900">
                {selected ? `${selected.label} · ${selected.acts.length} Acts` : 'Year not found'}
              </h2>
              {selected ? (
                <ActList acts={selected.acts} />
              ) : (
                <EmptyState title="No Acts for this year">Choose another year.</EmptyState>
              )}
            </section>
          ) : groups.length === 0 ? (
            <EmptyState title="No Acts yet">Acts appear here as they are uploaded.</EmptyState>
          ) : (
            <>
              <p className="text-sm text-ink-900" role="status" aria-live="polite">
                {total} Acts across {groups.length} years
              </p>
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
                {groups.map((g) => (
                  <li key={g.key}>
                    <Link
                      to={`?year=${g.key}`}
                      className="flex h-full flex-col gap-1 rounded-xl border border-line-200 bg-white p-4 hover:border-navy-900 hover:bg-navy-50"
                    >
                      <span className="font-display text-2xl text-navy-900">{g.label}</span>
                      <span className="text-sm text-muted-600">
                        {g.acts.length} {g.acts.length === 1 ? 'Act' : 'Acts'}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : null}
    </Page>
  );
}

const savePdf = (data: Blob, title: string) => saveFile(data, title, PDF_FILE);

/**
 * The original document, exactly as uploaded, in the browser's PDF viewer, with a download button
 * below. The file is fetched with the signed-in user's token and held in memory.
 */
export function OriginalReader({
  tokens,
  doc,
}: {
  tokens: IdentityClient;
  doc: { versionId: string; title: string };
}) {
  const [params] = useSearchParams();
  const page = Number(params.get('page')) || 1;
  const original = useQuery({
    queryKey: ['library', 'original', doc.versionId],
    queryFn: () => libraryApi.original(tokens, doc.versionId),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!original.data) return;
    const objectUrl = URL.createObjectURL(original.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [original.data]);

  if (original.isPending) return <LoadingState label="Loading the document" />;
  if (original.isError) {
    return (
      <ErrorState title="The document is not available" onRetry={() => void original.refetch()}>
        {original.error.message}
      </ErrorState>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {url ? (
        <iframe
          key={page}
          src={`${url}#page=${page}`}
          title={`${displayTitle(doc.title)} — document reader`}
          className="h-[80vh] w-full rounded-lg border border-line-200 bg-white"
        />
      ) : null}
      <Button
        className="self-start"
        onClick={() => {
          setSaveError(null);
          savePdf(original.data, displayTitle(doc.title)).catch(() =>
            setSaveError('The PDF could not be saved. Please try again.'),
          );
        }}
      >
        <Download aria-hidden /> Download PDF
      </Button>
      {saveError ? (
        <p role="alert" className="text-sm text-ink-900">
          {saveError}
        </p>
      ) : null}
    </div>
  );
}

const NOT_YET_REVIEWED: SourceVerification = {
  authenticity: { verdict: 'pending' },
  text_integrity: { verdict: 'pending' },
  legal_status: { verdict: 'pending' },
  legal_analysis: { verdict: 'pending' },
};

/**
 * The editorial verdicts on one document (ADR-023), each dimension separately, never as one
 * "verified" flag. Quiet while loading; a document no one has reviewed shows every dimension
 * as pending.
 */
export function SourceVerdicts({
  tokens,
  versionId,
}: {
  tokens: IdentityClient;
  versionId: string;
}) {
  const reviews = useQuery({
    queryKey: ['library', 'reviews'],
    queryFn: () => libraryApi.reviews(tokens),
    staleTime: 60_000,
  });
  if (!reviews.data) return null;
  const review = reviews.data.reviews.find((r) => r.versionId === versionId);
  const state = STATUS_LABEL[review?.state ?? 'review_ready'];
  return (
    <details className="rounded-lg border border-line-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-medium text-navy-900">
        Editorial verification: {state.label}
      </summary>
      <div className="mt-3">
        <VerificationDimensions verification={review?.verification ?? NOT_YET_REVIEWED} />
      </div>
    </details>
  );
}

export function LibraryActPage() {
  const tokens = useReaderTokens();
  const { versionId = '' } = useParams();
  const catalogue = useCatalogue(tokens);
  if (!tokens) return <Denied />;
  const act = catalogue.data?.acts.find((a) => a.versionId === versionId);
  return (
    <Page title={(act ? displayTitle(act.title) : null) ?? 'Act'} width="wide">
      <BackLinks
        fallback={act?.year ? `/library?year=${act.year}` : '/library'}
        fallbackLabel="Back to the library"
      />
      <ProvisionalNotice />
      {catalogue.isPending ? <LoadingState label="Loading the Act" /> : null}
      {catalogue.isError ? (
        <ErrorState title="This Act could not be loaded" onRetry={() => void catalogue.refetch()}>
          {catalogue.error.message}
        </ErrorState>
      ) : null}
      {catalogue.data && !act ? (
        <EmptyState title="Act not found">
          It is not in the library available to you. It may not have been uploaded yet.
        </EmptyState>
      ) : null}
      {act ? <SourceVerdicts tokens={tokens} versionId={act.versionId} /> : null}
      {act ? <ReportProblem versionId={act.versionId} context="library" /> : null}
      {act ? <OriginalReader tokens={tokens} doc={act} /> : null}
    </Page>
  );
}

/** Ask Buakei about the Acts (ADR-017), for every verified account, workspace or not. */
export function LibraryAskPage() {
  const tokens = useReaderTokens();
  useEffect(() => {
    document.title = 'Ask Buakei · Buakei AI';
  }, []);
  if (!tokens) return <Denied />;
  // A workspace filling the area below the app header: toolbar, conversation, composer.
  return (
    <div className="flex h-full min-h-0 flex-col px-3 pt-3 md:px-6 md:pt-4">
      <h1 className="sr-only">Ask Buakei</h1>
      <ActsAssistant tokens={tokens} fill back={{ to: '/library', label: 'Back to library' }} />
    </div>
  );
}
